import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminApi } from '../hooks/useAdminApi';
import { CreditCard, CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { get } = useAdminApi();
  const { user } = useAdminAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const provider = searchParams.get('provider');
  const paypalToken = searchParams.get('token');

  const pollStatus = useCallback(async () => {
    if (provider === 'paypal' && paypalToken) {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await fetch(`${API_URL}/api/saas/payment/paypal/execute`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ token: paypalToken }),
        });
        const data = await res.json();
        setStatus(data.success ? 'success' : 'error');
      } catch {
        setStatus('error');
      }
      return;
    }

    if (!sessionId) { setStatus('error'); return; }

    const res = await get(`/saas/payment/status/${sessionId}`);
    if (res.success) {
      if (res.data.payment_status === 'paid') {
        setStatus('success');
      } else if (attempts < 5) {
        setAttempts(prev => prev + 1);
        setTimeout(() => pollStatus(), 2000);
      } else {
        setStatus('pending');
      }
    } else {
      setStatus('error');
    }
  }, [sessionId, provider, paypalToken, get, attempts]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { pollStatus(); }, []);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center max-w-md w-full">
        {status === 'checking' && (
          <>
            <Loader2 size={48} className="text-amber-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Verification du paiement...</h2>
            <p className="text-slate-400">Veuillez patienter</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Paiement reussi !</h2>
            <p className="text-slate-400 mb-6">Votre abonnement est maintenant actif.</p>
            <button
              onClick={() => navigate('/admin/fleet')}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-lg transition-colors"
              data-testid="go-to-fleet-btn"
            >
              Acceder a la flotte
            </button>
          </>
        )}
        {status === 'pending' && (
          <>
            <Loader2 size={48} className="text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Paiement en cours de traitement</h2>
            <p className="text-slate-400 mb-6">Votre paiement est en attente de confirmation. Vous recevrez un email de confirmation.</p>
            <button onClick={() => navigate('/admin/fleet')} className="text-amber-500 hover:underline">
              Continuer vers la flotte
            </button>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Erreur de paiement</h2>
            <p className="text-slate-400 mb-6">Une erreur est survenue. Veuillez reessayer.</p>
            <button onClick={() => navigate('/admin/settings')} className="text-amber-500 hover:underline">
              Retour aux parametres
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const PaymentCancelPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center max-w-md w-full">
        <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <XCircle size={32} className="text-amber-500" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">Paiement annule</h2>
        <p className="text-slate-400 mb-6">Votre essai gratuit est toujours actif.</p>
        <button
          onClick={() => navigate('/admin/settings')}
          className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-6 py-3 rounded-lg transition-colors"
          data-testid="back-to-settings-btn"
        >
          Retour aux parametres
        </button>
      </div>
    </div>
  );
};

const PaymentButton = ({ planSlug, billingCycle, onLoading }) => {
  const [loading, setLoading] = useState(null);

  const handlePayment = async (provider) => {
    setLoading(provider);
    if (onLoading) onLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const originUrl = window.location.origin;

      const res = await fetch(`${API_URL}/api/saas/payment/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          provider,
          plan: planSlug,
          billing_cycle: billingCycle,
          origin_url: originUrl,
        }),
      });

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.detail || 'Erreur de paiement');
      }
    } catch {
      alert('Erreur de connexion');
    }
    setLoading(null);
    if (onLoading) onLoading(false);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="payment-buttons">
      <button
        onClick={() => handlePayment('stripe')}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 bg-[#635bff] hover:bg-[#7a73ff] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
        data-testid="pay-stripe-btn"
      >
        {loading === 'stripe' ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <CreditCard size={18} />
        )}
        Payer avec Stripe
      </button>
      <button
        onClick={() => handlePayment('paypal')}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 bg-[#0070ba] hover:bg-[#003087] text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
        data-testid="pay-paypal-btn"
      >
        {loading === 'paypal' ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <span className="font-bold text-lg">P</span>
        )}
        Payer avec PayPal
      </button>
    </div>
  );
};

export { PaymentSuccessPage, PaymentCancelPage, PaymentButton };
export default PaymentButton;
